import { useEffect, useState } from 'react'
import { ChevronUp } from 'lucide-react'
import { agentService } from '../services/agentService'
import type { MenuItem } from '../types'

interface MenuDrawerProps {
  propertyId: string
}

/** Category order is the server's order, so a Map rather than an object. */
function groupByCategory(items: MenuItem[]): Map<string, MenuItem[]> {
  const byCategory = new Map<string, MenuItem[]>()
  for (const item of items) {
    const category = item.category || 'Other'
    const existing = byCategory.get(category)
    if (existing) existing.push(item)
    else byCategory.set(category, [item])
  }
  return byCategory
}

/**
 * The menu the agent is working from.
 *
 * Fetched from the agent rather than the backend on purpose: the page must
 * never show a menu the agent does not have. If it cannot be loaded there is no
 * drawer at all — a customer can still order by talking, which is the feature.
 */
export function MenuDrawer({ propertyId }: MenuDrawerProps): JSX.Element | null {
  const [categories, setCategories] = useState<Map<string, MenuItem[]> | null>(null)
  const [currency, setCurrency] = useState('')
  const [open, setOpen] = useState(false)
  const [openCategory, setOpenCategory] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    agentService
      .menu(propertyId)
      .then((menu) => {
        if (cancelled) return
        setCurrency(menu.currency ?? '')
        setCategories(groupByCategory(menu.items ?? []))
      })
      .catch(() => {
        // No drawer. Nothing to tell the customer — they came here to talk.
      })
    return () => { cancelled = true }
  }, [propertyId])

  if (!categories || categories.size === 0) return null

  return (
    // A flex sibling of the stage, not an overlay. The prototype shipped a bug
    // where the transcript painted over the open drawer; taking real layout
    // space makes that impossible rather than a z-index to remember.
    <div className="flex max-h-[60vh] shrink-0 flex-col border-t border-white/10 bg-[#111118]">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex shrink-0 items-center justify-between px-5 py-3.5 text-[14px] font-semibold text-white/85"
        aria-expanded={open}
      >
        Menu
        <ChevronUp
          size={16}
          className={`text-white/40 transition-transform duration-200 ${open ? '' : 'rotate-180'}`}
        />
      </button>

      {open && (
        // Tapping the drawer's own background closes whatever category is open.
        <div className="overflow-y-auto pb-2" onClick={() => setOpenCategory(null)}>
          {[...categories].map(([category, items]) => (
            <div key={category}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  // One category at a time; tapping the open one collapses it.
                  setOpenCategory((current) => (current === category ? null : category))
                }}
                className={`flex w-full items-center justify-between border-t border-white/[0.06] px-5 py-2.5 text-[14px] ${
                  openCategory === category ? 'text-brand-green' : 'text-white/75'
                }`}
                aria-expanded={openCategory === category}
              >
                <span>{category}</span>
                <span className="text-white/35">{items.length}</span>
              </button>

              {openCategory === category && (
                <div className="px-5 pb-2">
                  {items.map((item) => {
                    // A missing flag means available — the agent reads it the
                    // same defensive way.
                    const sold = item.available === false
                    return (
                      <div
                        key={item.id}
                        className={`flex justify-between gap-4 py-1 text-[13px] ${
                          sold ? 'text-white/25 line-through' : 'text-white/55'
                        }`}
                      >
                        <span>{item.name}</span>
                        {/* Money is a string from the backend. Shown as sent,
                            never parsed; a null price renders as nothing. */}
                        <span className="shrink-0">
                          {item.price ? `${item.price} ${currency}` : ''}
                        </span>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
